'use client';

import React, { useState } from 'react';
import Link from 'next/link';

interface ServiceFeature {
  icon: string;
  title: string;
  description: string;
}

interface ProcessStep {
  step: string;
  title: string;
  description: string;
}

interface ServiceStat {
  value: string;
  label: string;
}

interface ServiceData {
  slug: string;
  icon: string;
  badge: string;
  title: string;
  subtitle: string;
  heroImage: string;
  heroAlt: string;
  accentColor: string;
  description: string;
  features: ServiceFeature[];
  process: ProcessStep[];
  stats: ServiceStat[];
}

interface Props {
  service: ServiceData;
}

const otherServices = [
  { slug: 'medecine-generale', label: 'Médecine Générale', icon: 'fa-solid fa-stethoscope' },
  { slug: 'medecine-specialisee', label: 'Médecine Spécialisée', icon: 'fa-solid fa-heart-pulse' },
  { slug: 'telemedecine', label: 'Télémédecine', icon: 'fa-solid fa-laptop-medical' },
  { slug: 'analyses-biomedicales', label: 'Analyses Biomédicales', icon: 'fa-solid fa-flask' },
  { slug: 'evacuation-sanitaire', label: 'Évacuation Sanitaire', icon: 'fa-solid fa-truck-medical' },
  { slug: 'soins-preventifs', label: 'Soins Préventifs', icon: 'fa-solid fa-shield-heart' },
];

export default function CosaServiceLayout({ service }: Props) {
  const [scrolled] = useState(false);

  const css = `
    :root { --primary: #0e5932; --accent: #fbaf45; --bg: #F0F7F3; --text: #7D7D75; --dark: #1a1a1a; }
    * { box-sizing: border-box; }
    html { scroll-behavior: smooth; }
    .svc-page { font-family: 'Onest', sans-serif; background: var(--bg); color: var(--text); }
    .svc-page a { text-decoration: none; }
    .svc-page p { line-height: 1.7em; margin: 0; }
    .svc-page h1,.svc-page h2,.svc-page h3 { font-weight: 700; line-height: 1.2em; margin: 0; }
    .svc-container { max-width: 1300px; margin: 0 auto; padding: 0 20px; }
    .svc-topbar { background: var(--accent); padding: 10px 0; }
    .svc-header { background: white; box-shadow: 0 2px 20px rgba(0,0,0,0.08); position: sticky; top: 0; z-index: 999; }
    .svc-nav { display: flex; justify-content: space-between; align-items: center; height: 76px; }
    .svc-btn { display: inline-flex; align-items: center; gap: 8px; font-size: 14px; font-weight: 600; background: var(--accent); color: white; border-radius: 100px; padding: 12px 22px; border: none; cursor: pointer; transition: all 0.3s; font-family: 'Onest', sans-serif; text-decoration: none; }
    .svc-btn:hover { background: var(--primary); }
    .svc-btn.outline { background: transparent; color: var(--primary); border: 2px solid var(--primary); }
    .svc-btn.outline:hover { background: var(--primary); color: white; }
    .svc-hero { position: relative; height: 520px; overflow: hidden; }
    .svc-hero-img { width: 100%; height: 100%; object-fit: cover; }
    .svc-hero-overlay { position: absolute; inset: 0; background: linear-gradient(135deg, rgba(14,89,50,0.88) 0%, rgba(14,89,50,0.55) 60%, rgba(14,89,50,0.2) 100%); }
    .svc-hero-content { position: absolute; inset: 0; display: flex; align-items: center; z-index: 2; }
    .svc-breadcrumb { display: flex; align-items: center; gap: 8px; font-size: 13px; color: rgba(255,255,255,0.7); margin-bottom: 16px; flex-wrap: wrap; }
    .svc-breadcrumb a { color: rgba(255,255,255,0.7); transition: color 0.3s; }
    .svc-breadcrumb a:hover { color: var(--accent); }
    .svc-breadcrumb span { color: var(--accent); }
    .svc-badge { display: inline-flex; align-items: center; gap: 8px; font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; color: white; background: rgba(251,175,69,0.2); border: 1px solid rgba(251,175,69,0.4); border-radius: 100px; padding: 6px 14px; margin-bottom: 14px; }
    .svc-hero-title { font-size: clamp(32px, 5vw, 60px); font-weight: 800; color: white; margin-bottom: 14px; }
    .svc-hero-sub { font-size: 17px; color: rgba(255,255,255,0.85); max-width: 560px; line-height: 1.7em; }
    .svc-stats-bar { background: var(--primary); padding: 28px 0; }
    .svc-stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; }
    .svc-stat { text-align: center; padding: 10px; border-right: 1px solid rgba(255,255,255,0.15); }
    .svc-stat:last-child { border-right: none; }
    .svc-stat-val { font-size: 36px; font-weight: 800; color: var(--accent); line-height: 1; margin-bottom: 6px; }
    .svc-stat-label { font-size: 13px; color: rgba(255,255,255,0.75); font-weight: 500; }
    .svc-main { padding: 80px 0; }
    .svc-grid { display: grid; grid-template-columns: 1fr 340px; gap: 50px; align-items: start; }
    .svc-section-badge { display: inline-flex; align-items: center; gap: 8px; font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; color: var(--primary); background: rgba(14,89,50,0.1); border-radius: 100px; padding: 6px 14px; margin-bottom: 12px; }
    .svc-section-h2 { font-size: clamp(24px, 3vw, 38px); font-weight: 700; color: var(--dark); margin-bottom: 18px; }
    .svc-desc { font-size: 16px; line-height: 1.8em; color: var(--text); margin-bottom: 36px; }
    .svc-features-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 18px; margin-bottom: 48px; }
    .svc-feature-card { padding: 22px; border-radius: 14px; background: white; border: 1px solid rgba(14,89,50,0.08); transition: all 0.4s ease; }
    .svc-feature-card:hover { border-color: var(--accent); box-shadow: 0 8px 30px rgba(14,89,50,0.1); transform: translateY(-3px); }
    .svc-feature-icon { width: 50px; height: 50px; border-radius: 12px; background: rgba(14,89,50,0.08); display: flex; align-items: center; justify-content: center; margin-bottom: 14px; transition: all 0.4s; }
    .svc-feature-card:hover .svc-feature-icon { background: var(--accent); }
    .svc-feature-card:hover .svc-feature-icon i { color: white !important; }
    .svc-feature-title { font-size: 15px; font-weight: 700; color: var(--dark); margin-bottom: 8px; }
    .svc-feature-desc { font-size: 14px; line-height: 1.6em; color: var(--text); }
    .svc-process { margin-bottom: 48px; }
    .svc-process-title { font-size: 22px; font-weight: 700; color: var(--dark); margin-bottom: 22px; }
    .svc-process-steps { display: flex; flex-direction: column; gap: 0; }
    .svc-process-step { display: flex; gap: 18px; padding-bottom: 24px; position: relative; }
    .svc-process-step::before { content: ''; position: absolute; left: 19px; top: 42px; bottom: 0; width: 2px; background: rgba(14,89,50,0.12); }
    .svc-process-step:last-child::before { display: none; }
    .svc-process-num { width: 40px; height: 40px; border-radius: 50%; background: var(--primary); border: 3px solid var(--accent); flex-shrink: 0; display: flex; align-items: center; justify-content: center; color: white; font-size: 11px; font-weight: 800; z-index: 1; }
    .svc-process-body h4 { font-size: 15px; font-weight: 700; color: var(--dark); margin-bottom: 4px; }
    .svc-process-body p { font-size: 14px; color: var(--text); line-height: 1.6em; }
    .svc-sidebar { position: sticky; top: 96px; }
    .svc-sidebar-card { background: white; border-radius: 16px; padding: 26px; border: 1px solid rgba(14,89,50,0.08); margin-bottom: 18px; }
    .svc-sidebar-title { font-size: 16px; font-weight: 700; color: var(--dark); margin-bottom: 16px; padding-bottom: 12px; border-bottom: 2px solid rgba(14,89,50,0.1); }
    .svc-service-link { display: flex; align-items: center; gap: 12px; padding: 11px 14px; border-radius: 10px; font-size: 14px; font-weight: 500; color: var(--dark); transition: all 0.3s; margin-bottom: 4px; }
    .svc-service-link:hover, .svc-service-link.active { background: rgba(14,89,50,0.07); color: var(--primary); }
    .svc-service-link.active { border-left: 3px solid var(--accent); }
    .svc-service-link i { width: 32px; height: 32px; border-radius: 8px; background: rgba(14,89,50,0.08); display: flex; align-items: center; justify-content: center; font-size: 13px; color: var(--primary); flex-shrink: 0; }
    .svc-cta-card { background: linear-gradient(135deg, var(--primary) 0%, #0a4226 100%); border-radius: 16px; padding: 28px; text-align: center; }
    .svc-cta-card h3 { font-size: 20px; font-weight: 700; color: white; margin-bottom: 10px; }
    .svc-cta-card p { font-size: 14px; color: rgba(255,255,255,0.8); margin-bottom: 20px; line-height: 1.6em; }
    .svc-hours { background: rgba(255,255,255,0.08); border-radius: 10px; padding: 14px; margin-top: 16px; }
    .svc-hours li { display: flex; justify-content: space-between; padding: 7px 0; border-bottom: 1px solid rgba(255,255,255,0.1); font-size: 13px; color: rgba(255,255,255,0.8); }
    .svc-hours li:last-child { border-bottom: none; }
    .svc-hours li span { font-weight: 600; color: var(--accent); }
    .svc-footer-bar { background: var(--primary); padding: 22px 0; margin-top: 60px; }
    .svc-footer-inner { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 14px; }
    .svc-footer-inner p { color: rgba(255,255,255,0.75); font-size: 14px; }
    @media (max-width: 1024px) {
      .svc-grid { grid-template-columns: 1fr; }
      .svc-sidebar { position: static; }
      .svc-stats-grid { grid-template-columns: repeat(2, 1fr); }
    }
    @media (max-width: 768px) {
      .svc-features-grid { grid-template-columns: 1fr; }
      .svc-hero { height: 380px; }
      .svc-stats-grid { grid-template-columns: repeat(2, 1fr); }
    }
  `;

  return (
    <div className="svc-page">
      <style>{css}</style>

      {/* TOPBAR */}
      <div className="svc-topbar">
        <div className="svc-container" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <p style={{ margin: 0, fontSize: 13, color: 'white', fontWeight: 500 }}>
            Votre Santé, Notre Mission —{' '}
            <Link href="/#contact" style={{ color: 'white', fontWeight: 700, textDecoration: 'underline' }}>Prendre Rendez-vous</Link>
          </p>
          <div style={{ display: 'flex', gap: 14 }}>
            {['fa-brands fa-facebook-f', 'fa-brands fa-linkedin-in', 'fa-brands fa-whatsapp'].map((icon, i) => (
              <a key={i} href="#" style={{ color: 'white', fontSize: 14 }}><i className={icon}></i></a>
            ))}
          </div>
        </div>
      </div>

      {/* HEADER */}
      <div className="svc-header">
        <div className="svc-container">
          <nav className="svc-nav">
            <Link href="/" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <svg viewBox="0 0 44 44" width="40" height="40" fill="none">
                <circle cx="22" cy="22" r="20" fill="#0e5932" stroke="#fbaf45" strokeWidth="2" />
                <ellipse cx="22" cy="22" rx="9" ry="20" stroke="#fbaf45" strokeWidth="1.5" fill="none" />
                <line x1="2" y1="22" x2="42" y2="22" stroke="#fbaf45" strokeWidth="1.5" />
                <line x1="5" y1="14" x2="39" y2="14" stroke="#fbaf45" strokeWidth="1" opacity="0.6" />
                <line x1="5" y1="30" x2="39" y2="30" stroke="#fbaf45" strokeWidth="1" opacity="0.6" />
              </svg>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', lineHeight: 1 }}>
                  <span style={{ fontSize: 20, fontWeight: 800, color: '#0e5932', letterSpacing: '0.06em' }}>C</span>
                  <span style={{ fontSize: 20, fontWeight: 800, color: '#fbaf45', letterSpacing: '0.06em' }}>O</span>
                  <span style={{ fontSize: 20, fontWeight: 800, color: '#0e5932', letterSpacing: '0.06em' }}>SA</span>
                </div>
                <div style={{ fontSize: 9, color: '#7D7D75', letterSpacing: '0.04em', marginTop: 2 }}>Compagnie de Santé Africaine</div>
              </div>
            </Link>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Link href="/#services" className="svc-btn outline">Tous les Services</Link>
              <Link href="/#contact" className="svc-btn">Rendez-vous</Link>
            </div>
          </nav>
        </div>
      </div>

      {/* HERO */}
      <div className="svc-hero">
        <img src={service.heroImage} alt={service.heroAlt} className="svc-hero-img" />
        <div className="svc-hero-overlay" />
        <div className="svc-hero-content">
          <div className="svc-container">
            <div className="svc-breadcrumb">
              <Link href="/">Accueil</Link>
              <i className="fa-solid fa-chevron-right" style={{ fontSize: 9 }}></i>
              <Link href="/#services">Services</Link>
              <i className="fa-solid fa-chevron-right" style={{ fontSize: 9 }}></i>
              <span>{service.badge}</span>
            </div>
            <div className="svc-badge">
              <i className={service.icon}></i>
              {service.badge}
            </div>
            <h1 className="svc-hero-title">{service.title}</h1>
            <p className="svc-hero-sub">{service.subtitle}</p>
          </div>
        </div>
      </div>

      {/* STATS BAR */}
      <div className="svc-stats-bar">
        <div className="svc-container">
          <div className="svc-stats-grid">
            {service.stats.map((stat, i) => (
              <div key={i} className="svc-stat">
                <div className="svc-stat-val">{stat.value}</div>
                <div className="svc-stat-label">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div className="svc-main">
        <div className="svc-container">
          <div className="svc-grid">
            {/* LEFT CONTENT */}
            <div>
              <span className="svc-section-badge">
                <i className={service.icon}></i>
                {service.badge}
              </span>
              <h2 className="svc-section-h2">À propos de ce service</h2>
              <p className="svc-desc">{service.description}</p>

              {/* FEATURES */}
              <h3 style={{ fontSize: 20, fontWeight: 700, color: '#1a1a1a', marginBottom: 20 }}>Ce que nous proposons</h3>
              <div className="svc-features-grid">
                {service.features.map((feat, i) => (
                  <div key={i} className="svc-feature-card">
                    <div className="svc-feature-icon">
                      <i className={feat.icon} style={{ color: '#0e5932', fontSize: 18 }}></i>
                    </div>
                    <div className="svc-feature-title">{feat.title}</div>
                    <p className="svc-feature-desc">{feat.description}</p>
                  </div>
                ))}
              </div>

              {/* PROCESS */}
              <div className="svc-process">
                <h3 className="svc-process-title">Comment ça fonctionne ?</h3>
                <div className="svc-process-steps">
                  {service.process.map((step, i) => (
                    <div key={i} className="svc-process-step">
                      <div className="svc-process-num">{step.step}</div>
                      <div className="svc-process-body">
                        <h4>{step.title}</h4>
                        <p>{step.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA BANNER */}
              <div style={{ background: 'linear-gradient(135deg, rgba(14,89,50,0.06) 0%, rgba(251,175,69,0.08) 100%)', border: '1px solid rgba(14,89,50,0.12)', borderRadius: 16, padding: '28px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap' }}>
                <div>
                  <h3 style={{ fontSize: 20, fontWeight: 700, color: '#1a1a1a', marginBottom: 6 }}>Prêt à consulter ?</h3>
                  <p style={{ fontSize: 15, color: '#7D7D75' }}>Prenez rendez-vous dès aujourd'hui avec nos équipes médicales.</p>
                </div>
                <Link href="/#contact" className="svc-btn" style={{ flexShrink: 0 }}>
                  <i className="fa-solid fa-calendar-plus"></i>
                  Prendre Rendez-vous
                </Link>
              </div>
            </div>

            {/* SIDEBAR */}
            <div className="svc-sidebar">
              {/* Services list */}
              <div className="svc-sidebar-card">
                <div className="svc-sidebar-title">Nos Services Médicaux</div>
                {otherServices.map((s) => (
                  <Link key={s.slug} href={`/services/${s.slug}`} className={`svc-service-link ${s.slug === service.slug ? 'active' : ''}`}>
                    <i className={s.icon}></i>
                    {s.label}
                  </Link>
                ))}
              </div>

              {/* CTA Card */}
              <div className="svc-cta-card">
                <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(251,175,69,0.2)', border: '2px solid rgba(251,175,69,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                  <i className="fa-solid fa-phone" style={{ color: '#fbaf45', fontSize: 20 }}></i>
                </div>
                <h3>Besoin d'aide ?</h3>
                <p>Notre équipe est disponible pour répondre à toutes vos questions médicales.</p>
                <Link href="/#contact" className="svc-btn" style={{ width: '100%', justifyContent: 'center' }}>
                  Nous Contacter
                </Link>
                <ul className="svc-hours" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  <li>Lun – Ven <span>8h – 18h</span></li>
                  <li>Samedi <span>8h – 14h</span></li>
                  <li>Dimanche <span>Urgences</span></li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* FOOTER BAR */}
      <div className="svc-footer-bar">
        <div className="svc-container">
          <div className="svc-footer-inner">
            <p>© 2024 COSA – Compagnie de Santé Africaine. Tous droits réservés.</p>
            <div style={{ display: 'flex', gap: 20 }}>
              <Link href="/" style={{ color: 'rgba(255,255,255,0.75)', fontSize: 14, transition: 'color 0.3s' }}>Accueil</Link>
              <Link href="/#services" style={{ color: 'rgba(255,255,255,0.75)', fontSize: 14 }}>Services</Link>
              <Link href="/#contact" style={{ color: 'rgba(255,255,255,0.75)', fontSize: 14 }}>Contact</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
